// @championcare/ui — shared React primitives for the web apps. These wrap the class contract in
// styles.css so apps compose products from primitives rather than re-styling each page (DESIGN.md
// "Shared Primitives"). New variants are added only when a real product state requires them.
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
} from "react";
import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

/**
 * Light/dark theme controller. Reads a persisted choice (localStorage `cc-theme`), falling back to
 * the OS preference, and reflects it as `data-theme` on <html> — which the token contract keys off
 * (`[data-theme="dark"]`). Each app's origin keeps its own persisted choice.
 */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof document === "undefined") return "light";
    const stored = localStorage.getItem("cc-theme");
    if (stored === "light" || stored === "dark") return stored;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem("cc-theme", theme);
    } catch {
      /* private mode / storage disabled — non-fatal */
    }
  }, [theme]);
  const toggle = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  return { theme, setTheme, toggle };
}

/** Round moon/sun button that flips the theme. Inherits its color from context. */
export function ThemeToggle({ className, ...rest }: HTMLAttributes<HTMLButtonElement>) {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <button
      type="button"
      className={cx("cc-theme-toggle", className)}
      onClick={toggle}
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      aria-pressed={dark}
      {...rest}
    >
      {dark ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

type BrandProps = {
  /** Type size in px for the wordmark; the shield mark scales with it. */
  size?: number;
  /** Light wordmark for dark surfaces (e.g. the marketing masthead). */
  onDark?: boolean;
} & HTMLAttributes<HTMLSpanElement>;

/**
 * Champion Care brand lockup — crimson shield-plus mark + "Champion" / crimson italic "Care"
 * wordmark (Outfit, via --font-display). Inline SVG, theme-aware. Markup mirrors the `.brand` element in
 * championcare-design. No raster logo.
 */
export function Brand({ size = 22, onDark, className, style, ...rest }: BrandProps) {
  const icon = Math.round(size * 1.27);
  // `onDark` = a permanently dark surface (the masthead, the member card), so the wordmark must
  // stay light regardless of theme. Use a fixed cream, NOT var(--cream) — that token flips to a
  // dark value under [data-theme="dark"], which would turn "Champion" dark on the dark bar.
  const wordColor = onDark ? "#FAF6F0" : "var(--ink)";
  const careColor = onDark ? "var(--red-soft)" : "var(--red)";
  return (
    <span
      className={cx("cc-brand", className)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: Math.round(size * 0.45),
        fontFamily: "var(--font-display)",
        fontWeight: 600,
        fontSize: size,
        letterSpacing: "-0.02em",
        lineHeight: 1,
        color: wordColor,
        ...style,
      }}
      {...rest}
    >
      <svg
        width={icon}
        height={icon}
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <path
          d="M16 2L4 8v9c0 7.5 5.1 14.3 12 15 6.9-0.7 12-7.5 12-15V8L16 2z"
          stroke="var(--red)"
          strokeWidth="1.5"
          fill="rgba(200,39,58,0.08)"
        />
        <path d="M16 12v8M12 16h8" stroke="var(--red)" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>
        Champion<em style={{ fontStyle: "italic", color: careColor, fontWeight: 500 }}>Care</em>
      </span>
    </span>
  );
}

type BtnVariant = "primary" | "ghost" | "gold";

type BtnProps = {
  variant?: BtnVariant;
  children: ReactNode;
} & (
  | ({ as: "a" } & AnchorHTMLAttributes<HTMLAnchorElement>)
  | ({ as?: "button" } & ButtonHTMLAttributes<HTMLButtonElement>)
);

/** Primary action primitive. Renders a `<button>` by default or an `<a>` with `as="a"`. */
export function Btn(props: BtnProps) {
  const variant = props.variant ?? "primary";
  if (props.as === "a") {
    const { as: _as, variant: _v, className, children, ...rest } = props;
    return (
      <a className={cx("btn", `btn--${variant}`, className)} {...rest}>
        {children}
      </a>
    );
  }
  const { as: _as, variant: _v, className, children, ...rest } = props;
  return (
    <button className={cx("btn", `btn--${variant}`, className)} {...rest}>
      {children}
    </button>
  );
}

type SurfaceProps = {
  inset?: boolean;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>;

/** Panel container. `inset` selects the secondary `surface-2` token. */
export function Surface({ inset, className, children, ...rest }: SurfaceProps) {
  return (
    <div className={cx(inset ? "surface-2" : "surface", className)} {...rest}>
      {children}
    </div>
  );
}

/** Small uppercase label that sits above section titles. */
export function Eyebrow({ className, children, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={cx("eyebrow", className)} {...rest}>
      {children}
    </span>
  );
}

type PillVariant = "default" | "gold" | "blood";

/** Status / label pill. */
export function Pill({
  variant = "default",
  className,
  children,
  ...rest
}: { variant?: PillVariant } & HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cx("badge", variant !== "default" && `badge--${variant}`, className)}
      {...rest}
    >
      {children}
    </span>
  );
}

/** Full-bleed content band with a centered container. */
export function Section({ className, children, ...rest }: HTMLAttributes<HTMLElement>) {
  return (
    <section className={cx("section", className)} {...rest}>
      <div className="container">{children}</div>
    </section>
  );
}

type FieldProps = {
  label: string;
  children: ReactNode;
} & LabelHTMLAttributes<HTMLLabelElement>;

/** Labeled form control wrapper. Pair with `<input className="input" />`. */
export function Field({ label, className, children, ...rest }: FieldProps) {
  return (
    <label className={cx("field", className)} {...rest}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

/** Token-styled text input. */
export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx("input", className)} {...rest} />;
}
